import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Pencil, Plus, Trash2, Check, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDate, fmtDayMonth, fmtMoney, fmtNumber, todayIso } from "@/lib/format";
import { StatusPill, LevelBadge, ProgressBar, CpIcon, cpLabel } from "@/components/Badges";
import SponsorForm from "@/components/SponsorForm";
import CounterpartForm from "@/components/CounterpartForm";

function Header({ s, onEdit }) {
  return (
    <section className="card p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5 fade-up" data-testid="sponsor-header">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="sponsor-name">{s.name}</h1>
          <LevelBadge level={s.level} prefix testId="sponsor-level-badge" />
        </div>
        <div className="flex flex-wrap gap-x-7 gap-y-1.5 mt-3 text-sm text-[#5B665F]">
          <span data-testid="sponsor-amount"><b className="text-[#13201A]">{fmtMoney(s.amount)}</b> par saison · {s.paid >= s.amount ? "réglé" : `${fmtMoney(s.paid)} encaissés`}</span>
          <span data-testid="sponsor-dates">Contrat du <b className="text-[#13201A]">{fmtDate(s.start_date)}</b> au <b className="text-[#13201A]">{fmtDate(s.end_date)}</b></span>
          {s.contact_name && <span data-testid="sponsor-contact">Contact : <b className="text-[#13201A]">{s.contact_name}</b>{s.contact_role && `, ${s.contact_role}`}{s.phone && ` · ${s.phone}`}</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button className="btn-ghost" onClick={onEdit} data-testid="sponsor-edit-button"><Pencil size={15} /> Modifier</button>
        <a className="btn-outline" href={`mailto:${encodeURIComponent(s.email)}`} data-testid="sponsor-send-message-button">Envoyer un message</a>
        <Link className="btn-primary" to={`/bilans/${s.id}`} data-testid="sponsor-generate-bilan-button">Générer le bilan</Link>
      </div>
    </section>
  );
}

function Counterparts({ s, reload, onOpen }) {
  const delivered = s.counterparts.filter((c) => c.status === "livre").length;
  const toggle = async (c) => {
    try {
      await api.patch(`/counterparts/${c.id}/deliver`, { delivered: c.status !== "livre" });
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <section className="card overflow-hidden" data-testid="counterparts-card">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-6 py-5 border-b border-[#E1E4DD]">
        <h2 className="text-lg font-bold">Contreparties promises</h2>
        <div className="flex items-center gap-3"><div className="w-28 shrink-0"><ProgressBar value={delivered} total={s.counterparts.length} className="h-1.5" /></div><span className="text-sm whitespace-nowrap" data-testid="counterparts-progress">{delivered} sur {s.counterparts.length} livrées</span></div>
      </div>
      {s.counterparts.map((c) => (
        <div key={c.id} className={`flex items-start gap-3 sm:gap-4 px-5 sm:px-6 py-4 border-b border-[#E1E4DD] ${c.display_status === "en_retard" ? "bg-[#FFF8EE]" : ""}`} data-testid={`counterpart-${c.id}`}>
          <CpIcon status={c.display_status} />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[15px]">{c.title}</p>
            <p className="text-sm text-[#5B665F] mt-0.5">
              {c.status === "livre" ? `Livré le ${fmtDate(c.delivered_date)}` : `Prévu le ${fmtDate(c.due_date)}`}{c.detail && ` · ${c.detail}`}
              {c.photo_path && <span className="inline-flex items-center gap-1 ml-1"> · <ImageIcon size={13} /> photo</span>}
            </p>
            <label className="inline-flex items-center gap-2 mt-2 text-[13px] font-semibold cursor-pointer select-none">
              <input type="checkbox" className="w-4 h-4 accent-[#1E6B45]" checked={c.status === "livre"} onChange={() => toggle(c)} data-testid={`counterpart-deliver-checkbox-${c.id}`} />
              Livré
            </label>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusPill status={c.display_status} testId={`counterpart-status-${c.id}`}>{cpLabel[c.display_status]}</StatusPill>
            <button className="text-[#5B665F] hover:text-[#13201A] p-1" onClick={() => onOpen(c)} title="Modifier" data-testid={`counterpart-edit-${c.id}`}><Pencil size={14} /></button>
          </div>
        </div>
      ))}
      {s.counterparts.length === 0 && <p className="px-6 py-6 text-sm text-[#5B665F]">Aucune contrepartie pour l'instant.</p>}
      <div className="px-5 sm:px-6 py-4">
        <button className="btn-outline btn-sm border-dashed" onClick={() => onOpen(null)} data-testid="add-counterpart-button"><Plus size={15} /> Ajouter une contrepartie</button>
      </div>
    </section>
  );
}

function MiniForm({ onSubmit, placeholder, testId }) {
  const [f, setF] = useState({ date: todayIso(), text: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (await onSubmit(f)) setF({ date: todayIso(), text: "" });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 pt-4 mt-2 border-t border-[#E1E4DD]">
      <div className="flex gap-2">
        <input type="date" required className="field h-10 w-[150px] shrink-0" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`${testId}-date-input`} />
        <input required className="field h-10" placeholder={placeholder} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid={`${testId}-text-input`} />
      </div>
      <button className="btn-outline btn-sm self-start" data-testid={`${testId}-submit`}><Plus size={14} /> Ajouter</button>
    </form>
  );
}

function SideCard({ title, children, testId }) {
  return <section className="card p-5 sm:p-6" data-testid={testId}><h2 className="text-lg font-bold mb-4">{title}</h2>{children}</section>;
}

function Reminders({ s, reload }) {
  const today = todayIso();
  const act = async (fn) => { try { await fn(); reload(); return true; } catch (e) { toast.error(apiError(e)); return false; } };
  return (
    <SideCard title="Rappels" testId="reminders-card">
      <ul className="space-y-3">
        {s.reminders.map((r) => {
          const late = !r.done && r.date < today;
          return (
            <li key={r.id} className="flex gap-3 text-sm group" data-testid={`reminder-${r.id}`}>
              <button onClick={() => act(() => api.patch(`/reminders/${r.id}`, { done: !r.done }))} className={`mt-0.5 w-4 h-4 rounded border grid place-items-center shrink-0 ${r.done ? "bg-[#1E6B45] border-[#1E6B45] text-white" : "border-[#B9BFB5]"}`} data-testid={`reminder-toggle-${r.id}`} aria-label="Fait">{r.done && <Check size={11} />}</button>
              <span className={`w-16 shrink-0 font-semibold ${late ? "text-[#B86200]" : "text-[#5B665F]"}`} data-testid={`reminder-date-${r.id}`}>{fmtDayMonth(r.date)}</span>
              <span className={`flex-1 ${r.done ? "line-through text-[#5B665F]" : late ? "text-[#8A4B00]" : ""}`}>{r.text}</span>
              <button onClick={() => act(() => api.delete(`/reminders/${r.id}`))} className="text-[#5B665F] opacity-60 hover:opacity-100" data-testid={`reminder-delete-${r.id}`} aria-label="Supprimer"><Trash2 size={13} /></button>
            </li>
          );
        })}
        {s.reminders.length === 0 && <li className="text-sm text-[#5B665F]">Aucun rappel.</li>}
      </ul>
      <MiniForm placeholder="Nouveau rappel" testId="reminder-form" onSubmit={(f) => act(() => api.post(`/sponsors/${s.id}/reminders`, f))} />
    </SideCard>
  );
}

function History({ s, reload }) {
  const act = async (fn) => { try { await fn(); reload(); return true; } catch (e) { toast.error(apiError(e)); return false; } };
  return (
    <SideCard title="Historique" testId="history-card">
      <ul className="space-y-3">
        {s.history.map((h) => (
          <li key={h.id} className="flex gap-3 text-sm" data-testid={`history-${h.id}`}>
            <span className="w-16 shrink-0 font-semibold text-[#5B665F]">{fmtDayMonth(h.date)}</span>
            <span className="flex-1">{h.text}</span>
            {!h.auto && <button onClick={() => act(() => api.delete(`/history/${h.id}`))} className="text-[#5B665F] opacity-60 hover:opacity-100" data-testid={`history-delete-${h.id}`} aria-label="Supprimer"><Trash2 size={13} /></button>}
          </li>
        ))}
      </ul>
      <MiniForm placeholder="Ajouter une note" testId="note-form" onSubmit={(f) => act(() => api.post(`/sponsors/${s.id}/history`, f))} />
    </SideCard>
  );
}

const VIS = [["home_matches", "Matchs à domicile"], ["spectators", "Spectateurs cumulés"], ["posts", "Publications"], ["reach", "Personnes touchées en ligne"]];

function Visibility({ s, reload }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(s.visibility);
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/sponsors/${s.id}`, { visibility: Object.fromEntries(VIS.map(([k]) => [k, Number(v[k] || 0)])) });
      toast.success("Chiffres enregistrés"); setEdit(false); reload();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <SideCard title="Chiffres de visibilité" testId="visibility-card">
      <form onSubmit={save} className="grid grid-cols-2 gap-3">
        {VIS.map(([k, label]) => (
          <div key={k} className="bg-[#F8F9F6] rounded-[10px] p-3">
            {edit ? <input type="number" min="0" className="field h-9" value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`visibility-${k}-input`} />
              : <p className="num text-xl font-bold" data-testid={`visibility-${k}`}>{fmtNumber(s.visibility[k])}</p>}
            <p className="text-xs text-[#5B665F] mt-1">{label}</p>
          </div>
        ))}
        <div className="col-span-2">
          {edit ? <button className="btn-primary btn-sm" data-testid="visibility-save-button">Enregistrer</button>
            : <button type="button" className="btn-outline btn-sm" onClick={() => { setV(s.visibility); setEdit(true); }} data-testid="visibility-edit-button"><Pencil size={14} /> Modifier</button>}
        </div>
      </form>
    </SideCard>
  );
}

export default function SponsorDetail() {
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [cp, setCp] = useState({ open: false, item: null });
  const load = useCallback(() => api.get(`/sponsors/${id}`).then((r) => setS(r.data)).catch(() => setS(false)), [id]);
  useEffect(() => { load(); }, [load]);
  if (s === false) return <p data-testid="sponsor-not-found">Sponsor introuvable. <Link to="/sponsors" className="link">Retour</Link></p>;
  if (!s) return <p className="text-[#5B665F]">Chargement…</p>;
  return (
    <div data-testid="sponsor-detail-page">
      <p className="text-sm text-[#5B665F] mb-5"><Link to="/sponsors" className="link" data-testid="breadcrumb-sponsors">Sponsors</Link> / {s.name} · Saison {s.season}</p>
      <Header s={s} onEdit={() => setEditOpen(true)} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-6">
        <Counterparts s={s} reload={load} onOpen={(item) => setCp({ open: true, item })} />
        <div className="space-y-6">
          <Reminders s={s} reload={load} />
          <History s={s} reload={load} />
          <Visibility key={JSON.stringify(s.visibility)} s={s} reload={load} />
        </div>
      </div>
      <SponsorForm open={editOpen} onOpenChange={setEditOpen} sponsor={s} onSaved={load} />
      <CounterpartForm open={cp.open} item={cp.item} sponsorId={s.id} onOpenChange={(o) => setCp({ ...cp, open: o })} onSaved={load} />
    </div>
  );
}
